import { LoadingButton } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import { TextField } from 'components/ui/fields';
import { ModalActionButton, ModalActions, ModalContent, useModal } from 'components/ui/modal';
import { Formik, FormikErrors, FormikProps } from 'formik';
import { Participant, QuickSearchEntity } from 'middleware-types';
import { useCommunicationsContext } from '../communications-provider';
import { convertEntityType, removeTagsFromString } from '../helpers/utils';
import { useCreateConversation } from '../hooks/conversations-hooks';
import { MessageBox } from '../shared/message-box';
import { useValidation } from 'utils/useValidation';
import { ParticipantSearchField } from '../shared/participant-search-field';

interface NewConversationFormValues {
	subject: string;
	participants: QuickSearchEntity[];
	messageText: string;
}

export const CreateConversationModal = () => {
	const { entityId } = useCommunicationsContext();
	const { closeModal } = useModal();

	const initialValues: NewConversationFormValues = {
		subject: '',
		participants: [],
		messageText: '',
	};

	const validate = (values: NewConversationFormValues) => {
		const errors: FormikErrors<NewConversationFormValues> = {};
		if (!removeTagsFromString(values.messageText)) errors.messageText = 'Required Field';
		return errors;
	};

	const validation = useValidation('NewConversationRequest');

	const createConversation = useCreateConversation();
	const onSubmit = async (values: NewConversationFormValues) => {
		console.log(typeof values.messageText);
		const participants: Participant[] = values.participants.map((p) => ({
			entityId: p.id,
			entityType: convertEntityType(p.entityType),
		}));
		await createConversation({
			subject: values.subject,
			participants,
			messageText: values.messageText,
		}).then((success) => {
			if (success) closeModal();
		});
	};

	return (
		<Formik<NewConversationFormValues>
			onSubmit={onSubmit}
			initialValues={initialValues}
			validate={validate}
			validationSchema={validation.schema}>
			{(props: FormikProps<NewConversationFormValues>) => (
				<>
					<ModalContent>
						<Stack spacing={1}>
							<TextField label="Conversation Name" required name="subject" />
							<ParticipantSearchField
								name="participants"
								label="Participants"
								helperText="You can add or remove participants later."
								required
								omittedIds={[entityId]}
							/>
							<Stack spacing={0.5}>
								<Typography variant="h6">Initial Message *</Typography>
								<Stack
									px={1.5}
									py={0.5}
									border="1px solid"
									borderColor="neutral.300"
									borderRadius={1}
									minHeight={200}
									maxHeight={300}>
									<MessageBox />
								</Stack>
							</Stack>
						</Stack>
					</ModalContent>
					<ModalActions>
						<ModalActionButton variant="outlined">Cancel</ModalActionButton>
						<LoadingButton
							type="submit"
							color="primary"
							variant="contained"
							disabled={!props.isValid || !props.dirty}
							loading={props.isSubmitting}
							onClick={() => {
								props.submitForm();
							}}>
							Save
						</LoadingButton>
					</ModalActions>
				</>
			)}
		</Formik>
	);
};
